import baseFunc from '../../config/base_func.js';
import config from '../../config/config.js';

const QINIU_HOST = (config.qiniuMediaDomain || '').replace(/^\/\//, '');
const ALIOSS_HOST = (config.aliOSS?.outDomain || '').replace(/^\/\//, '');

export function isQiniuMediaUrl(url = '') {
	if (!url || typeof url !== 'string') return false;
	if (url.startsWith('blob:')) return false;
	const host = url.replace(/^https?:\/\//i, '').replace(/^\/\//, '').split('/')[0];
	return host === QINIU_HOST;
}

export function isAliossMediaUrl(url = '') {
	if (!url || typeof url !== 'string') return false;
	if (url.startsWith('blob:')) return false;
	const host = url.replace(/^https?:\/\//i, '').replace(/^\/\//, '').split('/')[0];
	return host === ALIOSS_HOST;
}

export function isSignedQiniuUrl(url = '') {
	return Boolean(url && /[?&]sign=/.test(url) && /[?&]t=/.test(url));
}

export function isSignedAliossUrl(url = '') {
	return Boolean(url && /[?&]auth_key=/.test(url));
}

/**
 * 七牛时间防盗链签名，与项目内 $baseFunc.getQiniusign 一致。
 * 无论入参是否已签名，都会重新签名（getQiniusign 会剥离旧 sign/t）。
 * @param {string} url 附件地址
 * @param {number|string} [frame] 传值时取视频帧封面
 */
export function getQiniuSignedUrl(url, frame) {
	if (!url) return '';
	if (url.startsWith('blob:')) return url;
	if (!isQiniuMediaUrl(url)) return url;
	return baseFunc.getQiniusign(url, frame);
}

/** 取未签名的原始地址（用于提交生成数据） */
export function resolveRawMediaUrl(item = {}) {
	if (!item || typeof item !== 'object') return '';
	if (item.url) return item.url;
	if (item.previewUrl && !isSignedQiniuUrl(item.previewUrl) && !isSignedAliossUrl(item.previewUrl)) {
		return item.previewUrl;
	}
	return '';
}

/** 编辑器预览地址：始终重新签名后返回 */
export function resolveSignedPreviewUrl(item = {}, { frame } = {}) {
	if (!item || typeof item !== 'object') return '';
	if (item.local || item.previewUrl?.startsWith('blob:')) {
		return item.previewUrl || item.url || '';
	}
	const raw = item.previewUrl || item.url || resolveRawMediaUrl(item);
	if (!raw) return '';
	if (isAliossMediaUrl(raw)) {
		return baseFunc.getAliOssAuthKey(raw);
	}
	return getQiniuSignedUrl(raw, frame);
}

/** 组件展示用：优先返回可访问的签名地址 */
export function getMediaItemUrl(item = {}, { frame } = {}) {
	const signed = resolveSignedPreviewUrl(item, { frame });
	if (signed) return signed;
	return item?.previewUrl || item?.url || '';
}

/**
 * 对富文本 HTML 字符串中的所有七牛图片/视频链接重新签名，
 * 解决存入数据库的签名链接过期问题。
 * 渲染前统一签名，存原始地址，彻底规避 2 小时失效。
 * 同时清洗危险链接协议（javascript: / data: / vbscript: / file:），
 * 避免历史数据中的脏链接被执行。
 * @param {string} html
 * @returns {string} 签名后的 HTML
 */
export function reSignRichTextMedia(html) {
	if (!html || typeof document === 'undefined') return html;

	let doc;
	try {
		const parser = new DOMParser();
		doc = parser.parseFromString(html, 'text/html');
	} catch {
		return html;
	}

	// 1. 清洗危险链接协议：javascript: / data: / vbscript: / file: / about:
	doc.querySelectorAll('a').forEach((el) => {
		const href = (el.getAttribute('href') || '').trim();
		if (!href) return;
		if (/^(javascript|data|vbscript|file|about):/i.test(href)) {
			// 去掉 href，保留文本和样式
			el.removeAttribute('href');
		} else {
			// 补齐 rel，防 window.opener / Referer 泄露
			if (!el.getAttribute('rel')) {
				el.setAttribute('rel', 'noopener noreferrer nofollow');
			}
		}
	});

	// 2. 重新签名七牛图片/视频
	['img', 'video'].forEach((tag) => {
		const els = doc.querySelectorAll(tag);
		els.forEach((el) => {
			// <img src="...">   <video src="..." poster="...">
			const attrs = tag === 'img' ? ['src'] : ['src', 'poster'];
			attrs.forEach((attr) => {
				const raw = el.getAttribute(attr);
				if (!raw) return;
				if (isQiniuMediaUrl(raw)) {
					el.setAttribute(attr, getQiniuSignedUrl(raw));
				} else if (isAliossMediaUrl(raw)) {
					el.setAttribute(attr, baseFunc.getAliOssAuthKey(raw));
				}
			});
		});
	});

	// 3. 视频默认显示播放控件 + 内联播放属性（兼容历史数据）
	doc.querySelectorAll('video').forEach((v) => {
		if (!v.hasAttribute('controls')) v.setAttribute('controls', 'controls');
		if (!v.hasAttribute('playsinline')) v.setAttribute('playsinline', 'true');
		if (!v.hasAttribute('webkit-playsinline')) v.setAttribute('webkit-playsinline', 'true');
		if (!v.hasAttribute('x5-playsinline')) v.setAttribute('x5-playsinline', 'true');
		// 多视频共存时只预加载元数据，节省带宽
		v.setAttribute('preload', 'metadata');
	});

	try {
		return doc.body.innerHTML;
	} catch {
		return html;
	}
}
